import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useSignedUrl } from '@/hooks/useSignedUrl'
import { cacheImageBlob, getImageFromCache, invalidateImageCache } from '@/lib/image-cache'
import { cn } from '@/lib/utils'

type AuthImageProps = {
  src: string
  alt: string
  className?: string
  fallback?: React.ReactNode
  decoding?: 'async' | 'sync' | 'auto'
  /** Lazy loading を有効にする（デフォルト: true） */
  lazy?: boolean
  /** Intersection Observer の rootMargin（デフォルト: '200px'） */
  rootMargin?: string
  /** 再試行の最大回数（デフォルト: 2） */
  maxRetries?: number
  /**
   * アダプティブモード: 横長画像は親の高さに合わせてクロップ、縦長画像は幅固定で高さauto
   * widthClass で固定幅を指定（デフォルト: 'w-14'）
   */
  adaptive?: boolean
  /** adaptive モード時の固定幅クラス（デフォルト: 'w-14'） */
  widthClass?: string
  /** IndexedDB に画像を永続キャッシュするためのキー。指定するとLRU+TTLでキャッシュされる */
  cacheKey?: string
}

/**
 * 認証が必要かどうかを判定（相対パスの場合は認証が必要）
 */
const requiresAuth = (src: string): boolean => src.startsWith('/')

/**
 * 署名付きURLを使用する画像コンポーネント（Lazy Loading対応）
 * ビューポートに入った時にのみ署名付きURLを取得
 */
export const AuthImage = ({
  src,
  alt,
  className,
  fallback,
  decoding = 'async',
  lazy = true,
  rootMargin = '200px',
  maxRetries = 2,
  adaptive = false,
  widthClass = 'w-14',
  cacheKey,
}: AuthImageProps) => {
  const ref = useRef<HTMLDivElement>(null)
  const [isInView, setIsInView] = useState(!lazy)
  const [imageError, setImageError] = useState(false)
  const [retryCount, setRetryCount] = useState(0)
  const [isLandscape, setIsLandscape] = useState<boolean | null>(adaptive ? null : false)
  const [cachedUrl, setCachedUrl] = useState<string | null>(null)
  const [cacheChecked, setCacheChecked] = useState(!cacheKey)

  const needsAuth = useMemo(() => requiresAuth(src), [src])

  // Intersection Observer でビューポート監視
  useEffect(() => {
    if (!lazy || isInView || !ref.current) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true)
          observer.disconnect()
        }
      },
      { rootMargin },
    )

    observer.observe(ref.current)
    return () => observer.disconnect()
  }, [lazy, isInView, rootMargin])

  // パスを正規化（/api/プレフィックスを削除）
  // キャッシュヒット時は署名URL取得をスキップ
  const normalizedPath = useMemo(() => {
    if (!needsAuth || !isInView || cachedUrl) return null
    return src.startsWith('/api') ? src.slice(4) : src
  }, [src, needsAuth, isInView, cachedUrl])

  const { url: fullSignedUrl, isLoading, error, refetch } = useSignedUrl(normalizedPath)

  // 画像読み込みエラー時の再取得ハンドラ
  const handleImageError = useCallback(() => {
    // キャッシュから読み込んだ画像が壊れていた場合、キャッシュを破棄して署名URLフローへフォールバック
    if (cachedUrl) {
      URL.revokeObjectURL(cachedUrl)
      setCachedUrl(null)
      if (cacheKey) invalidateImageCache(cacheKey)
      return
    }

    if (retryCount < maxRetries) {
      setRetryCount((c) => c + 1)
      setImageError(false)
      refetch()
    } else {
      setImageError(true)
    }
  }, [cachedUrl, cacheKey, retryCount, maxRetries, refetch])

  // srcが変わったらリトライカウントをリセット
  useEffect(() => {
    setRetryCount(0)
    setImageError(false)
  }, [src])

  // cacheKey が指定されている場合、IndexedDB からキャッシュを確認
  useEffect(() => {
    if (!cacheKey || !isInView) return

    let revoked = false
    getImageFromCache(cacheKey).then((objectUrl) => {
      if (revoked) {
        if (objectUrl) URL.revokeObjectURL(objectUrl)
        return
      }
      if (objectUrl) {
        setCachedUrl(objectUrl)
      }
      setCacheChecked(true)
    })

    return () => {
      revoked = true
    }
  }, [cacheKey, isInView])

  // アンマウント時に Object URL を解放
  useEffect(() => {
    return () => {
      if (cachedUrl) URL.revokeObjectURL(cachedUrl)
    }
  }, [cachedUrl])

  // 画像表示成功後にキャッシュへ保存（cacheKey 指定時 & キャッシュミス時）
  const handleImageLoadWithCache = useCallback(
    (e: React.SyntheticEvent<HTMLImageElement>) => {
      if (adaptive) {
        const img = e.currentTarget
        setIsLandscape(img.naturalWidth > img.naturalHeight)
      }

      // 認証画像のみ IndexedDB キャッシュ（外部URLはブラウザ disk cache に任せる）
      if (cacheKey && !cachedUrl && fullSignedUrl && needsAuth) {
        // 絶対URLの場合は /api プロキシ経由に変換して CORS を回避
        let fetchUrl = fullSignedUrl
        try {
          const parsed = new URL(fullSignedUrl)
          fetchUrl = `/api${parsed.pathname}${parsed.search}`
        } catch {
          // 相対パスならそのまま使用
          if (!fetchUrl.startsWith('/api')) fetchUrl = `/api${fetchUrl}`
        }
        fetch(fetchUrl)
          .then((res) => (res.ok ? res.blob() : null))
          .then((blob) => {
            if (blob) cacheImageBlob(cacheKey, blob)
          })
          .catch(() => {})
      }
    },
    [adaptive, cacheKey, cachedUrl, fullSignedUrl, needsAuth],
  )

  // 最終的に表示する URL（キャッシュ優先）
  const displayUrl = cachedUrl || fullSignedUrl

  // 認証不要の場合（外部URLはブラウザ disk cache に任せる）
  if (!needsAuth) {
    return <img src={src} alt={alt} className={className} decoding={decoding} loading={lazy ? 'lazy' : undefined} />
  }

  // まだビューポートに入っていない場合はプレースホルダー
  if (!isInView) {
    return (
      <div ref={ref} className={className}>
        {fallback ?? <div className="w-full h-full bg-muted" />}
      </div>
    )
  }

  if (isLoading && !cachedUrl) {
    return fallback ? <>{fallback}</> : <div className={`${className} bg-muted animate-pulse`} />
  }

  // キャッシュ確認中（cacheKey 指定時のみ）
  if (!cacheChecked) {
    return fallback ? <>{fallback}</> : <div className={`${className} bg-muted animate-pulse`} />
  }

  if ((error && retryCount >= maxRetries && !cachedUrl) || imageError || !displayUrl) {
    return fallback ? (
      <>{fallback}</>
    ) : (
      <div className={`${className} bg-muted flex items-center justify-center`}>
        <span className="text-muted-foreground text-xs">読込失敗</span>
      </div>
    )
  }

  // アダプティブモード: ラッパーdivで横長画像は親の高さにフィット
  if (adaptive) {
    return (
      <div
        className={cn(
          widthClass,
          'shrink-0 overflow-hidden rounded-lg',
          isLandscape === true && 'self-stretch',
          className,
        )}
      >
        <img
          src={displayUrl}
          alt={alt}
          className={cn('rounded-lg object-cover', isLandscape === true ? 'h-full w-full' : `h-auto ${widthClass}`)}
          decoding={decoding}
          onLoad={handleImageLoadWithCache}
          onError={handleImageError}
        />
      </div>
    )
  }

  return (
    <img
      src={displayUrl}
      alt={alt}
      className={className}
      decoding={decoding}
      onLoad={cacheKey ? handleImageLoadWithCache : undefined}
      onError={handleImageError}
    />
  )
}
